import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { isAxiosError } from 'axios'

import { useMutation } from '../useMutation/useMutation'
import { useEducationHostedForm } from '../useEducationHostedForm/useEducationHostedForm'

import { sendOtpAction, verifyOtpAction } from '@percent/education-hosted-form/api/otp/otp.actions'
import { EducationHostedFormModel } from '@percent/education-hosted-form/app/EducationHostedForm/EducationHostedForm/EducationHostedForm.types'
import { APIErrorResponse } from '@percent/education-hosted-form/types/api.types'
import { ErrorBase } from '@percent/education-hosted-form/errors/ErrorBase'

export const OTP_COOLDOWN_SECONDS = 30

const isMaxAttemptsExceededError = (error: ErrorBase): boolean => {
  if (!isAxiosError<APIErrorResponse>(error.cause)) return false

  return error.cause.response?.data?.error?.code === 'validation_invite/otp_max_attempts_exceeded'
}

export type OtpErrorKey =
  | 'otp.errors.invalidCode'
  | 'otp.errors.expired'
  | 'otp.errors.rateLimit'
  | 'otp.errors.generic'

const verifyErrorKeyForResponse = (error: ErrorBase): OtpErrorKey => {
  if (!isAxiosError<APIErrorResponse>(error.cause)) return 'otp.errors.generic'

  const status = error.cause.response?.status
  const reasons = error.cause.response?.data?.error?.reasons ?? []

  // Terminal cases (max-attempts) short-circuit before reaching this
  // mapper. Anything that does reach here is a non-terminal error.

  if (status === 429) return 'otp.errors.rateLimit'

  if (status === 422) {
    // BE puts the specific cause in `reasons[]`; `message` is just the generic 422 phrase.
    // TODO (AV-2541): replace this regex with an error.code check once BE adds a stable
    // code for the "no active OTP" 422 case (per PR review on #6123).
    if (reasons.some(r => /no active otp/i.test(r))) return 'otp.errors.expired'

    return 'otp.errors.invalidCode'
  }

  return 'otp.errors.generic'
}

const sendErrorKeyForResponse = (error: ErrorBase): OtpErrorKey => {
  if (!isAxiosError<APIErrorResponse>(error.cause)) return 'otp.errors.generic'

  if (error.cause.response?.status === 429) return 'otp.errors.rateLimit'

  return 'otp.errors.generic'
}

export const useOtp = ({
  onVerifySuccess,
  onMaxAttemptsExceeded
}: {
  onVerifySuccess: (otpJwt: string) => void
  onMaxAttemptsExceeded?: () => void
}) => {
  const { programId } = useParams<{ programId: string }>()
  const { getValues } = useFormContext<EducationHostedFormModel>()
  const { otpCooldownEndsAt, setOtpCooldown } = useEducationHostedForm()

  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [verifyErrorKey, setVerifyErrorKey] = useState<OtpErrorKey | null>(null)
  const [sendErrorKey, setSendErrorKey] = useState<OtpErrorKey | null>(null)

  useEffect(() => {
    if (otpCooldownEndsAt === null) {
      setCooldownSeconds(0)

      return () => undefined
    }

    const update = () => {
      const remaining = Math.max(0, Math.ceil((otpCooldownEndsAt - Date.now()) / 1000))
      setCooldownSeconds(remaining)
    }

    update()
    const interval = setInterval(update, 1000)

    return () => clearInterval(interval)
  }, [otpCooldownEndsAt])

  const { mutate: mutateSendOtp, isPending: isSending } = useMutation(sendOtpAction, {
    onError: (error: ErrorBase) => {
      setSendErrorKey(sendErrorKeyForResponse(error))

      if (!isAxiosError<APIErrorResponse>(error.cause)) return

      if (error.cause.response?.status === 429) {
        const retryAfter = parseInt(error.cause.response.headers?.['retry-after'] ?? '', 10)

        setOtpCooldown(Number.isFinite(retryAfter) ? retryAfter : OTP_COOLDOWN_SECONDS)
      }
    },
    onSuccess: () => {
      setSendErrorKey(null)
      setOtpCooldown(OTP_COOLDOWN_SECONDS)
    }
  })

  const { mutate: mutateVerifyOtp, isPending: isVerifying } = useMutation(verifyOtpAction, {
    onError: (error: ErrorBase) => {
      if (isMaxAttemptsExceededError(error)) {
        onMaxAttemptsExceeded?.()

        return
      }

      setVerifyErrorKey(verifyErrorKeyForResponse(error))
    },
    onSuccess: ({ data }) => {
      setVerifyErrorKey(null)
      onVerifySuccess(data.token)
    }
  })

  const sendOtp = useCallback(() => {
    if (!programId) return

    const { email, firstName } = getValues()

    if (!email || !firstName) return

    mutateSendOtp({ validationInviteId: programId, email, firstName })
  }, [getValues, mutateSendOtp, programId])

  const verifyOtp = useCallback(
    (code: string) => {
      if (!programId) return

      const { email } = getValues()

      if (!email) return

      setSendErrorKey(null)
      mutateVerifyOtp({ validationInviteId: programId, email, code })
    },
    [getValues, mutateVerifyOtp, programId]
  )

  const clearVerifyError = useCallback(() => setVerifyErrorKey(null), [])
  const clearSendError = useCallback(() => setSendErrorKey(null), [])

  return {
    sendOtp,
    verifyOtp,
    clearVerifyError,
    clearSendError,
    cooldownSeconds,
    canResend: (otpCooldownEndsAt !== null || sendErrorKey !== null) && cooldownSeconds === 0,
    isSending,
    isVerifying,
    verifyErrorKey,
    sendErrorKey
  }
}
